import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Table } from "@mui/material";
import { useTranslation } from "react-i18next";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";
import { EDIT_ACCESS, FULL_ACCESS } from "constants/AccessTypes/AccessTypes";
import { ModelContext } from "contexts/ModelDetailContext";
import TableStyle from "styles/application/TableStyle";
import { ModelTaskListSyncProvider } from "contexts/ModelTaskListSyncContext";
import { TaskDetailStoreProvider } from "contexts/TaskDetailContext";
import BulkActionBar from "./BulkActionBar";
import ModelTaskDialogs from "./ModelTaskDialogs";
import ModelTaskHeader from "./ModelTaskHeader";
import VirtualModelTaskRows from "./VirtualModelTaskRows";
import useModelTaskTableStyles from "./useModelTaskTableStyles";
import useTaskRowExpansion from "./hooks/useTaskRowExpansion";
import useExpandedTasksUrlSync from "./hooks/useExpandedTasksUrlSync";
import useTaskRowSelection from "./hooks/useTaskRowSelection";
import useModelTaskBulkActions from "./hooks/useModelTaskBulkActions";
import useModelTaskTableInstance from "./hooks/useModelTaskTableInstance";
import useAutoColumnWidths from "./hooks/useAutoColumnWidths";
import useContainerWidth from "./hooks/useContainerWidth";
import useModelTaskChildProps from "./hooks/useModelTaskChildProps";
import useTaskRowVirtualizer from "./hooks/useTaskRowVirtualizer";
import useScopedResizeObserverDefer from "./hooks/useScopedResizeObserverDefer";
import useMergedChangedIds from "./hooks/useMergedChangedIds";

const AT = TableStyle();
// Russ opens ten to twelve at once when pasting the same tools or questions
// across a batch, so the cap has to clear that with room to spare. Raised
// from 15 as a short-term bump pending a firm number for the actual need.
const DEFAULT_EXPANSION_CAP = 30;

const ModelTaskTable = ({
	data,
	columnDefs,
	columns,
	sorting,
	onSortingChange,
	handleDelete,
	deleteEndpoint,
	modelId,
	handleCopy,
	handleCopyTaskQuestion,
	externalChangedIds,
	customCaptions,
	totalTaskCount,
	refreshData,
	access,
	isLoading,
	hasNextPage,
	hasPreviousPage,
	isFetchingNextPage,
	isFetchingPreviousPage,
	fetchNextPage,
	fetchPreviousPage,
	isFetchNextPageError,
	isFetchPreviousPageError,
	initialExpandedIds,
	initialPrimaryId,
	chips = [],
	maxExpandedRows = DEFAULT_EXPANSION_CAP,
}) => {
	const { classes, cx } = useModelTaskTableStyles();
	const { t } = useTranslation();
	useScopedResizeObserverDefer();
	const [{ modelDetail }] = useContext(ModelContext);
	const isPublished = Boolean(modelDetail?.isPublished);
	const showSelection = (access === FULL_ACCESS || access === EDIT_ACCESS) && !isPublished;
	const scrollRef = useRef(null);
	const [taskPendingDeletion, setTaskPendingDeletion] = useState(null);
	const closeDeleteDialog = useCallback(() => setTaskPendingDeletion(null), []);
	const goToTop = useCallback(
		() => scrollRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" }),
		[]
	);
	const containerWidth = useContainerWidth(scrollRef, isLoading);
	const sizedColumnDefs = useAutoColumnWidths({
		columnDefs,
		columns,
		data,
		containerWidth,
		showSelection,
	});
	const { table, rows } = useModelTaskTableInstance({
		data,
		columnDefs: sizedColumnDefs,
		sorting,
		onSortingChange,
	});
	const { expandedIds, primaryId, expansionMessage, setExpanded, expandWithoutLimit } =
		useTaskRowExpansion({
			initialExpandedIds,
			initialPrimaryId,
			maxExpandedRows,
			taskCaption: customCaptions?.task?.toLowerCase() || "task",
		});
	useExpandedTasksUrlSync({ expandedIds, primaryId });
	// Changing sort or chip filters (including the free-text "Contains" chip)
	// replaces the visible rows, so the selection a user built up no longer
	// refers to what is on screen. Uncommitted text in the search box does
	// not affect this — only committing it as a chip changes what's loaded.
	const selectionScope = useMemo(() => JSON.stringify([sorting, chips]), [chips, sorting]);
	const selection = useTaskRowSelection({ rows, scope: selectionScope });
	const bulk = useModelTaskBulkActions({ modelId, refreshData, selection, customCaptions });
	const changedIds = useMergedChangedIds(bulk.changedIds, externalChangedIds);

	const { virtualizer, virtualRows, topPadding, bottomPadding, isNextPageStalled } =
		useTaskRowVirtualizer({
			rows,
			scrollRef,
			expandedIds,
			anchorTaskId: initialPrimaryId,
			onAnchorReached: expandWithoutLimit,
			hasNextPage,
			isFetchingNextPage,
			isFetchNextPageError,
			fetchNextPage,
		});
	const { rowProps, previousPage, nextPage } = useModelTaskChildProps({
		onRequestDelete: setTaskPendingDeletion,
		handleCopy,
		handleCopyTaskQuestion,
		modelId,
		customCaptions,
		access,
		totalTaskCount,
		refreshData,
		selection,
		hasPreviousPage,
		isFetchingPreviousPage,
		isFetchPreviousPageError,
		fetchPreviousPage,
		hasNextPage,
		isFetchingNextPage,
		// Folds a stall into the same retry row as a real fetch error - the copy
		// stays generic enough (no claim of a network cause) to cover both.
		isFetchNextPageError: isFetchNextPageError || isNextPageStalled,
		fetchNextPage,
	});

	// Sits above the isLoading branch so it survives the loading flash
	// onTaskAdded's anchored query triggers, which would otherwise recreate
	// the store and discard tab-fetched corrections.
	if (isLoading)
		return (
			<TaskDetailStoreProvider>
				<CircularProgress aria-label={t("message.loadingTasks")} />
			</TaskDetailStoreProvider>
		);
	return (
		<TaskDetailStoreProvider>
			<ModelTaskListSyncProvider modelId={modelId}>
				<ModelTaskDialogs
					taskPendingDeletion={taskPendingDeletion}
					taskCaption={customCaptions?.task}
					deleteEndpoint={deleteEndpoint}
					onCloseDeleteDialog={closeDeleteDialog}
					onDeleted={handleDelete}
					showSelection={showSelection}
					assign={bulk.assign}
					applyAssign={bulk.applyAssign}
					taskCaptionPlural={customCaptions?.taskPlural || "Tasks"}
					customCaptions={customCaptions}
					bulkDeleteOpen={bulk.bulkDeleteOpen}
					selectedTasks={selection.selectedRows}
					isDeleting={bulk.isDeleting}
					onCloseBulkDelete={bulk.closeBulkDelete}
					onConfirmBulkDelete={bulk.confirmBulkDelete}
				/>
				<AutoFitContentInScreen containsTable scrollRef={scrollRef} bottomGap={0}>
					{expansionMessage && <div role="status">{expansionMessage}</div>}
					<Table
						aria-label={customCaptions?.taskPlural || t("entity.task", { count: 2 })}
						aria-rowcount={totalTaskCount}
						className={classes.table}
					>
						<AT.TableHead>
							<ModelTaskHeader
								headerGroup={table.getHeaderGroups()[0]}
								classes={classes}
								cx={cx}
								selection={selection}
								showSelection={showSelection}
							/>
						</AT.TableHead>
						<VirtualModelTaskRows
							rows={rows}
							virtualRows={virtualRows}
							virtualizer={virtualizer}
							topPadding={topPadding}
							bottomPadding={bottomPadding}
							expandedIds={expandedIds}
							onExpandedChange={setExpanded}
							columns={columns}
							classes={classes}
							data={data}
							rowProps={rowProps}
							previousPage={previousPage}
							nextPage={nextPage}
							showSelection={showSelection}
							selection={selection}
							changedIds={changedIds}
							onGoToTop={goToTop}
						/>
					</Table>
				</AutoFitContentInScreen>
				{showSelection && (
					<BulkActionBar
						selectedCount={selection.selectedCount}
						taskCaptionPlural={customCaptions?.taskPlural || "Tasks"}
						onClear={selection.clear}
						actions={bulk.bulkActions}
						assignRelations={bulk.assignRelations}
						onAssign={(relationKey) =>
							bulk.assign.open(relationKey, selection.selectedRows.map((t) => t.id))
						}
					/>
				)}
			</ModelTaskListSyncProvider>
		</TaskDetailStoreProvider>
	);
};

ModelTaskTable.propTypes = {
	data: PropTypes.array.isRequired,
	columnDefs: PropTypes.array.isRequired,
	columns: PropTypes.arrayOf(PropTypes.string).isRequired,
	sorting: PropTypes.array.isRequired,
	onSortingChange: PropTypes.func.isRequired,
	isLoading: PropTypes.bool.isRequired,
	chips: PropTypes.array,
	deleteEndpoint: PropTypes.string.isRequired,
	externalChangedIds: PropTypes.instanceOf(Set),
};

export default ModelTaskTable;
